import React from 'react';
import { Dialog, Stack, TextField, Button, 
  DialogActions, DialogContent, DialogTitle, MenuItem, Typography, Box} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import {startOfWeek, addDays} from 'date-fns';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { useTranslation } from "react-i18next";

const MUST_BE_SET_TO = "MUST_BE_SET_TO";
const MIN_UTIL = "MIN_UTIL";
const TEMPERATURE = "TEMPERATURE";

const BINARY = "BINARY";
const HVAC = "HVAC";


const EditDialog = (props) => {

  const {open, data, onClose, onChange, onSave, onDelete, category, title, setPoints} = props;

  const { t } = useTranslation();

  const handleChange = (props) => {
      onChange(props.target.id, props.target.value);
  }

  const handleSelect = (field, value) => {
    onChange(field, value);
  }

  return (

    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {title}
      </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
          <TextField
                variant="standard"
                id="constraintType"
                label={t("Constraint type")}
                onChange={(event) => handleSelect("constraintType", event.target.value)}
                value={data.constraintType}
                select
                >
                  {(window.location.hostname.includes("staging") || !window.location.hostname.includes("balderton")) && (
                    category === BINARY?
                      <MenuItem key={MIN_UTIL} value={MIN_UTIL}>
                        {t("Minimum utilization")}
                      </MenuItem>
                    :
                      <MenuItem key={TEMPERATURE} value={TEMPERATURE}>
                        {t("Flexible target temperature")}
                      </MenuItem>
                  )}
                  <MenuItem key={MUST_BE_SET_TO} value={MUST_BE_SET_TO}>
                    {t("Must be")}
                  </MenuItem>
            </TextField>
            {
              data.constraintType === MIN_UTIL?
              <TextField
                variant="standard"
                id="value"
                label={t("Value")}
                type="number"
                value={data.value}
                onChange={handleChange}
              />
              : null
            }
            {
              data.constraintType === MUST_BE_SET_TO?
              <TextField
                variant="standard"
                id="value"
                label={t("Value")}
                value={data.value}
                onChange={(event) => handleSelect("value", event.target.value)}
                select
              >
                {
                  category===BINARY? 
                    (["ON", "OFF"]).map((item) => 
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    )
                  : setPoints.split(",").concat(["OFF"]).map((item) => 
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  )
                }
              </TextField>
              : null
            }
            {
              data.constraintType === TEMPERATURE?
              <>   
                <TextField
                        variant="standard"
                        id="ideal"
                        label={t("Ideal (C)")}
                        value={data.ideal}
                        onChange={(event) => handleSelect("ideal", event.target.value)}
                        select
                >
                  {
                    setPoints.split(",").map((item) => 
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    )
                  }
                </TextField>
                <TextField
                        variant="standard"
                        id="penalty"
                        label={t("Penalty")}
                        type="number"
                        value={data.penalty}
                        onChange={handleChange}
                />
              </>
              : null
            }
          </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSave}>{t("Save")}</Button>
        <Button onClick={() => onDelete()}>{t("Delete")}</Button>
        <Button onClick={onClose} autoFocus>{t("Cancel")}</Button>
      </DialogActions>
    </Dialog>

  )


}



const ConstraintsEditor = React.forwardRef((props, ref) => {

  const {
    category, 
    setPoints,
    mustBeSetTo,
    minUtil, 
    temperatureConstr,
    errorMessages,
    onChange
  } = props;

  const { t, i18n } = useTranslation();

  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editDialogData, setEditDialogData] = React.useState({});
  const [editDialogTitle, setEditDialogTitle] = React.useState(t("Edit constraint"));

  const [events, setEvents] = React.useState([]);

  const calculateDate = (dayOfWeekISO) => {
    const t = addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), dayOfWeekISO - 1)
    const day = ('0' + t.getDate()).slice(-2);
    const month = ('0' + (t.getMonth() + 1)).slice(-2);
    const year = t.getFullYear();
    return `${year}-${month}-${day}`;
  }

  const calculateDatePlusOne = (dayOfWeekISO) => {
    const t = addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), dayOfWeekISO )
    const day = ('0' + t.getDate()).slice(-2);
    const month = ('0' + (t.getMonth() + 1)).slice(-2);
    const year = t.getFullYear();
    return `${year}-${month}-${day}`;
  }

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'), {noSsr: true});

  React.useEffect(() => {

    const mustBeSetToEvents = mustBeSetTo?.map(
      (item) => (
        {
          id: item.id ?? uuidv4(),
          title: t("Must be") + " " + item.value,
          start: calculateDate(item.iso_week_day ?? 1) + "T" + item.start,
          end: item.end !== "24:00"? calculateDate(item.iso_week_day ?? 1) + "T" + item.end : calculateDatePlusOne(item.iso_week_day ?? 1) + "T00:00:00",
          value: item.value,
          constraintType: MUST_BE_SET_TO,
          ideal: "21", // default values in case constraint type changes
          minimum: "15",
          maximum: "25",
          allowance: "1",
          gamma: "1",
          rho: "1"
        }
      )
  ) ?? []

  const minUtilEvents = minUtil?.map(
    (item) => (
      {
        id: item.id ?? uuidv4(),
        title: t("Min. util.") + " " + item.value,
        start: calculateDate(item.iso_week_day ?? 1) + "T" + item.start,
        end: item.end !== "24:00"? calculateDate(item.iso_week_day ?? 1) + "T" + item.end : calculateDatePlusOne(item.iso_week_day ?? 1) + "T00:00:00",
        value: item.value,
        constraintType: MIN_UTIL,
        ideal: "21", // default values in case constraint type changes
        minimum: "15",
        maximum: "25",
        allowance: "1",
        gamma: "1",
        rho: "1"
      }
    )
  ) ?? []

    const temperatureConstrEvents = temperatureConstr?.map(
      (item) => (
        {
          id: item.id ?? uuidv4(),
          title: isSmallScreen? t("Temp.") + " " + item.ideal : t("Temp.") + " " + item.ideal + ` ${t("Penalty")}\xa0` + item.penalty,
          start: calculateDate(item.iso_week_day ?? 1) + "T" + item.start,
          end: item.end !== "24:00"? calculateDate(item.iso_week_day ?? 1) + "T" + item.end : calculateDatePlusOne(item.iso_week_day ?? 1) + "T00:00",
          ideal: item.ideal,
          penalty: item.penalty,
          constraintType: TEMPERATURE,
          value: "21", // default value in case constraint type changes
        }
      )
    ) ?? []

    setEvents(mustBeSetToEvents.concat(minUtilEvents).concat(temperatureConstrEvents));

  }, [mustBeSetTo,  minUtil, temperatureConstr, isSmallScreen])

  const toIsoWeekDay = (day) => {
    return (day + 6) % 7 + 1
  }

  function getConstraints() {
    return getConstraintsEvents(events)
  }

  function getConstraintsEvents(events) {

    const mustBeSetTo = events.filter((item)=>item.constraintType === MUST_BE_SET_TO).map(
      (item) => ({
        start: item.start.split("T")[1],
        end: item.end.split("T")[1] === "00:00"? "24:00" :item.end.split("T")[1],
        iso_week_day: toIsoWeekDay((new Date(item.start)).getDay()),
        value: item.value
      })
    )
    const minUtil = props.category === BINARY? events.filter((item)=>item.constraintType === MIN_UTIL).map(
      (item) => ({
        start: item.start.split("T")[1],
        end: item.end.split("T")[1] === "00:00"? "24:00" :item.end.split("T")[1],
        iso_week_day: toIsoWeekDay((new Date(item.start.split("T")[0])).getDay()),
        value: item.value
      })
    ) : null

    const temperature = props.category === HVAC? events.filter((item)=>item.constraintType === TEMPERATURE).map(
      (item) => ({
        start: item.start.split("T")[1],
        end: item.end.split("T")[1] === "00:00"? "24:00" :item.end.split("T")[1],
        iso_week_day: toIsoWeekDay((new Date(item.start.split("T")[0])).getDay()),
        ideal: item.ideal,
        penalty: item.penalty
      })
    ) : null

    return {
      mustBeSetTo: mustBeSetTo,
      minUtil: minUtil,
      temperature: temperature
    };
  }

  React.useImperativeHandle(ref, () => ({
    getConstraints
  }));


  const handleEdit = (id, value) => {
    setEditDialogData({...editDialogData, [id]:value});
  }

  const handleSaveChanges = () => {

    var newData = editDialogData;
    if (editDialogData.constraintType === MIN_UTIL) 
      editDialogData.title = "Min. util. " + editDialogData.value
    else if (editDialogData.constraintType === MUST_BE_SET_TO) 
      editDialogData.title = "Must be " + editDialogData.value
    else
      editDialogData.title =  isSmallScreen? t("Temp.") + " " + editDialogData.ideal : t("Temp.") + " " + editDialogData.ideal + ` ${t("Penalty")}\xa0` + editDialogData.penalty

    let newEvents;

    if (events.some((item)=>item.id === newData.id)){
      newEvents = events.map(
        (item) => item.id === newData.id ? newData : item
      )
    } else {
      newEvents = [...events, newData]
    }

    setEvents(newEvents);
    onChange(getConstraintsEvents(newEvents));

    setEditDialogOpen(false);
  }

  const toIsoString = (d) => {
    return `${d.getFullYear()}-${('0' + (d.getMonth() + 1)).slice(-2)}-${('0' + d.getDate()).slice(-2)}`
                     +`T${('0' + d.getHours()).slice(-2)}:${('0' + d.getMinutes()).slice(-2)}`;
  }

  const handleSelect = (selectInfo) => {

    let calendarApi = selectInfo.view.calendar;

    calendarApi.unselect()

    const start = selectInfo.start;
    const end = selectInfo.end;

    if (end.getHours() !== 0) {
      if ((end.getHours() < start.getHours()) || ((end.getHours() === start.getHours()) && (end.getMinutes() <= start.getMinutes()))){
        return;
      }
    }
      
    const newStart = toIsoString(start);

    const newEnd = (end.getHours() === 0) && (end.getMinutes() === 0) ? 
      `${addDays(start, 1).getFullYear()}-${('0' + (addDays(start, 1).getMonth() + 1)).slice(-2)}-${('0' + addDays(start, 1).getDate()).slice(-2)}`
      +`T00:00`
      :
      `${start.getFullYear()}-${('0' + (start.getMonth() + 1)).slice(-2)}-${('0' + start.getDate()).slice(-2)}`
      +`T${('0' + end.getHours()).slice(-2)}:${('0' + end.getMinutes()).slice(-2)}`
      ;


    setEditDialogTitle(t("Add constraint"));

    setEditDialogData(
      {
        id: uuidv4(),
        start: newStart,
        end: newEnd,
        constraintType: category === HVAC? MUST_BE_SET_TO : MIN_UTIL,
        value: "21",
        ideal: "21",
        penalty: "0.2",
        barColor: "#fcb711"
      }
    )

    setEditDialogOpen(true);
  }

  const handleEventChange = (data) => {

    const start = data.event.start;
    const end = data.event.end;

    if (end.getHours() !== 0) {
      if ((end.getHours() < start.getHours()) || ((end.getHours() === start.getHours()) && (end.getMinutes() <= start.getMinutes()))){
        setEvents(
          events => events.map((e)=>({...e}))
        )
        return;
      }
    }

    const newStart = toIsoString(start);

    const newEnd = (end.getHours() === 0) && (end.getMinutes() === 0) ? 
      `${addDays(start, 1).getFullYear()}-${('0' + (addDays(start, 1).getMonth() + 1)).slice(-2)}-${('0' + addDays(start, 1).getDate()).slice(-2)}`
      +`T00:00`
      :
      `${start.getFullYear()}-${('0' + (start.getMonth() + 1)).slice(-2)}-${('0' + start.getDate()).slice(-2)}`
      +`T${('0' + end.getHours()).slice(-2)}:${('0' + end.getMinutes()).slice(-2)}`
      ;

    let newEvents = events.map((e) => e.id === data.event.id? {...e, start: newStart, end: newEnd} : e)

    const diff = Math.floor((end - start) / 86400000);

    for (let i = 1; i <= diff; i++) {

      const extraStart = toIsoString(addDays(Date.parse(newStart), i));
      const extraEnd = toIsoString(addDays(Date.parse(newEnd), i));

      newEvents = [
        ...newEvents,
        {
          ...events.find((item)=>item.id===data.event.id),
          start: extraStart,
          end: extraEnd,
          id: uuidv4()
        }
      ]
    }

    setEvents(newEvents)

    onChange(getConstraintsEvents(newEvents))
    
  }

  const handleEventClick = (event) => {
    setEditDialogTitle(t("Edit constraint"));
    setEditDialogOpen(true);
    setEditDialogData(events.find((item)=>item.id === event.event.id));
  }

  const handleDelete = () => {

    if (editDialogData.id) {

      const newEvents = events.filter((e)=>e.id !== editDialogData.id);

      setEvents(events => newEvents)

      onChange(getConstraintsEvents(newEvents))

    }

    setEditDialogOpen(false);
  }

  

  const renderErrorMessages = (errorMessages) => {

    let dayName = {1:t("Monday"), 2: t("Tuesday"), 3: t("Wednesday"), 4: t("Thursday"), 5: t("Friday"), 6: t("Saturday"), 7: t("Sunday")};

    return(
      <>
        {
            errorMessages["must_be_set_to_constraints"]?
            Object.entries(errorMessages["must_be_set_to_constraints"]).map(
                ([key,value])=><Typography sx={{ color: "red" }} key={key}>Error in constraint {
                  dayName[getConstraints().mustBeSetTo[parseInt(key)].iso_week_day] + " " +
                  getConstraints().mustBeSetTo[parseInt(key)].start + "-" + getConstraints().mustBeSetTo[parseInt(key)].end
                  }. {
                    Object.entries(value).map(([key2, value2])=>key2==="non_field_errors"?`${value2}`:`${key2}: ${value2} `)
                }</Typography>
            )
            :null
        }
        {
            errorMessages["temperature_constraints"]?
            Object.entries(errorMessages["temperature_constraints"]).map(
                ([key,value])=><Typography sx={{ color: "red" }} key={key}>Error in constraint {
                  dayName[getConstraints().temperature[parseInt(key)].iso_week_day] + " " +
                  getConstraints().temperature[parseInt(key)].start + "-" + getConstraints().temperature[parseInt(key)].end
                  }. {
                    Object.entries(value).map(([key2, value2])=>key2==="non_field_errors"?`${value2}`:`${key2}: ${value2} `)
                }</Typography>
            )
            :null
        }
        {
            errorMessages["minimum_utilization_constraints"]?
            Object.entries(errorMessages["minimum_utilization_constraints"]).map(
                ([key,value])=><Typography sx={{ color: "red" }} key={key}>Error in constraint {
                  dayName[getConstraints().minUtil[parseInt(key)].iso_week_day] + " " +
                  getConstraints().minUtil[parseInt(key)].start + "-" + getConstraints().minUtil[parseInt(key)].end
                  }. {
                    Object.entries(value).map(([key2, value2])=>key2==="non_field_errors"?`${value2}`:`${key2}: ${value2} `)
                }</Typography>
            )
            :null
        }
      </>
    )
  }

  return (
    <>
      <EditDialog 
        category={category}
        setPoints={setPoints}
        open={editDialogOpen} 
        data={editDialogData}
        onClose={()=>setEditDialogOpen(false)}
        onChange={handleEdit}
        onSave={handleSaveChanges}
        onDelete={handleDelete}
        title={editDialogTitle}
      />
      <Stack height="100%">
        {renderErrorMessages(errorMessages)}
        <Box height={"100%"}>
        <FullCalendar
          height="100%"
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView='timeGridWeek'
          headerToolbar={null}
          allDaySlot={false}
          events={events}
          weekNumberCalculation={"ISO"}
          editable={true}
          eventChange={handleEventChange}
          eventClick={handleEventClick}
          select={handleSelect}
          selectable={true}
          dayHeaderFormat={(d)=>{return (new Date(...d.date.array)).toLocaleString(i18n.resolvedLanguage, {weekday:'short'})}}
          now="2023-01-01" //to prevent the calendar from highlighting today's day
          initialDate={new Date()}
        />
        </Box>    
      </Stack>
      
    </>
  )

  
})

export default ConstraintsEditor;